import { useEffect, useState } from 'react';

import {
  ChakraFlex,
  ChakraHStack,
  CheckboxV2,
  FAIconName,
  FireflyButton,
  ModalV2,
} from '@equip.health/ui';

import ClosedReasonDropdown from '~/components/common/inquiry/drawer/ClosedReasonDropdown';
import { DateTime } from 'luxon';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'inquiry-status-modal';

export interface InquiryCloseModalProps {
  appointments?: GetAppointmentDetailsResponse;
  isNotesEmpty?: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (props: { closedReasonId: string; noShowEmail?: boolean }) => void;
}

const modalArgs = {
  bodyStyle: {
    overflow: 'visible',
  },
  footerStyle: {
    padding: '0px',
  },
  titleStyle: {
    flex: '1 0 0',
    padding: '0px',
  },
};

export const canDisplayCheckbox = (
  appointments?: GetAppointmentDetailsResponse,
) => {
  if (!appointments) {
    return false;
  }
  const scheduledAppointments = appointments?.data;
  if (scheduledAppointments?.length > 0) {
    return !scheduledAppointments.some((appointment) => {
      const appointmentDate = DateTime.fromISO(
        appointment.appointmentStartDateTime,
      );
      return (
        appointmentDate > DateTime.now() &&
        appointment.appointmentStatus === AppointmentStatus.SCHEDULED
      );
    });
  }
  return false;
};

const InquiryCloseModal = ({
  appointments,
  isNotesEmpty,
  isOpen,
  onCancel,
  onConfirm,
}: InquiryCloseModalProps) => {
  const [closedReasonId, setClosedReasonId] = useState<string>();
  const [shouldShowNoShowEmailCheckbox, setShouldShowNoShowEmailCheckbox] =
    useState(false);
  const {
    appointmentQualifyingCallReschedule:
      isAppointmentQualifyingCallRescheduleEnabled,
  } = useFlags<LDFlags>();
  const [noShowEmail, setNoShowEmail] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setClosedReasonId(undefined);
      setShouldShowNoShowEmailCheckbox(false);
      setNoShowEmail(false);
    }
  }, [isOpen]);
  const onOptionClick = (val: string) => {
    setClosedReasonId(val);
    setNoShowEmail(false);
  };

  const handleConfirm = () => {
    onConfirm({ closedReasonId, noShowEmail });
  };

  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={onCancel}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__confirm`}
            isDisabled={!closedReasonId}
            leftIcon={FAIconName.checkRegular}
            onClick={handleConfirm}
          >
            Close inquiry
          </FireflyButton>
        </ChakraHStack>
      }
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={onCancel}
      title="Close inquiry"
      {...modalArgs}
    >
      <ChakraFlex flexDirection="column" gap="24px">
        <ClosedReasonDropdown
          handleNoShowEmailCheckbox={setShouldShowNoShowEmailCheckbox}
          id={TOP_LEVEL_ID}
          isRequired
          modalId={`${TOP_LEVEL_ID}__modal-modal`}
          onSelect={onOptionClick}
          placeholder="Please choose"
          value={closedReasonId}
        />

        {isAppointmentQualifyingCallRescheduleEnabled &&
          canDisplayCheckbox(appointments) &&
          isNotesEmpty &&
          shouldShowNoShowEmailCheckbox && (
            <CheckboxV2
              caption="Please only use for scheduled qualifying calls with no answer"
              id={`${TOP_LEVEL_ID}_showSessionSummary`}
              isChecked={noShowEmail}
              label='Send "QC no show" email to primary contact'
              onChange={setNoShowEmail}
            />
          )}
      </ChakraFlex>
    </ModalV2>
  );
};

export default InquiryCloseModal;
